import { Stack } from "@certn/cascadia";
import { Schemas } from "../../types";
import {
  DateSelector,
  SectionMessage,
  SectionSubHeading,
  TextField,
  Toggle,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatDateSelectValueAsISODate,
  parseISODateAsDateSelectValue,
} from "@certn/cascadia-react/components/inputs/date-input/utils";
import { css } from "@emotion/css";
import { hasMonthPrecision } from "./utils";
import isNil from "lodash/isNil";

type CVReviewEmploymentExtractionProps = {
  employment: Schemas["CVEmploymentItem"];
  originalEmployment?: Schemas["CVEmploymentItem"];
  handleEmploymentItemChange: (employment: Schemas["CVEmploymentItem"]) => void;
};

export const CVReviewEmploymentExtraction = ({
  employment,
  originalEmployment,
  handleEmploymentItemChange,
}: CVReviewEmploymentExtractionProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(
    isNil(employment.is_excluded) ? true : !employment.is_excluded
  );

  return (
    <Stack gap="space-2" className={css({ width: "100%" })}>
      <SectionSubHeading
        testID="employment-item-subheading"
        title={employment.company_name}
        isCollapsible
        noWrap
        isOpen={open}
        onClick={() => setOpen(!open)}
        detailText={employment.is_excluded ? t("Excluded") : undefined}
      />
      {open && (
        <Stack gap="space-4" className={css({ padding: "var(--space-2)" })}>
          {employment.is_excluded && (
            <SectionMessage
              type="info"
              sectionTitle={t(
                "This employment is outside the verification scope and will be excluded from the review."
              )}
              icon="info"
            />
          )}
          <TextField
            label={t("Company")}
            name="company_name"
            testID="employment-item-company"
            isRequired
            value={employment.company_name}
            onChange={(value) =>
              handleEmploymentItemChange({
                ...employment,
                company_name: value,
              })
            }
          />
          <TextField
            label={t("Position")}
            name="position"
            testID="employment-item-position"
            value={employment.position || undefined}
            onChange={(value) =>
              handleEmploymentItemChange({
                ...employment,
                position: value,
              })
            }
          />
          <Stack
            gap="space-2"
            orientation="horizontal"
            className={css({ width: "100%", "& > *": { flex: 1 } })}
          >
            <DateSelector
              label={t("Start year")}
              value={
                parseISODateAsDateSelectValue(employment.start_date) ||
                undefined
              }
              onChange={(value) => {
                const start_date = formatDateSelectValueAsISODate(value);

                handleEmploymentItemChange({
                  ...employment,
                  start_date: start_date || employment.start_date,
                });
              }}
              testID="employment-start-year"
              name="start_date"
              fullWidth
              formatOverride="yy"
            />
            <DateSelector
              label={t("Start month")}
              value={
                hasMonthPrecision(employment.start_date_precision)
                  ? parseISODateAsDateSelectValue(employment.start_date) ||
                    undefined
                  : undefined
              }
              onChange={(value) => {
                const start_date = formatDateSelectValueAsISODate(value);

                handleEmploymentItemChange({
                  ...employment,
                  start_date: start_date || employment.start_date,
                  start_date_precision: "month",
                });
              }}
              testID="employment-start-month"
              name="start_date"
              fullWidth
              formatOverride="mm"
            />
          </Stack>
          {!employment.is_current && (
            <Stack
              gap="space-2"
              orientation="horizontal"
              className={css({ width: "100%", "& > *": { flex: 1 } })}
            >
              <DateSelector
                label={t("End year")}
                value={
                  (employment.end_date &&
                    parseISODateAsDateSelectValue(employment.end_date)) ||
                  undefined
                }
                onChange={(value) => {
                  const end_date = formatDateSelectValueAsISODate(value);

                  handleEmploymentItemChange({
                    ...employment,
                    end_date,
                  });
                }}
                testID="employment-end-year"
                name="end_date"
                fullWidth
                formatOverride="yy"
              />
              <DateSelector
                label={t("End month")}
                value={
                  employment.end_date_precision &&
                  hasMonthPrecision(employment.end_date_precision)
                    ? (employment.end_date &&
                        parseISODateAsDateSelectValue(employment.end_date)) ||
                      undefined
                    : undefined
                }
                onChange={(value) => {
                  const end_date = formatDateSelectValueAsISODate(value);

                  handleEmploymentItemChange({
                    ...employment,
                    end_date,
                    end_date_precision: "month",
                  });
                }}
                testID="employment-end-month"
                name="end_date"
                fullWidth
                formatOverride="mm"
              />
            </Stack>
          )}
          <Toggle
            name="is_current"
            isSelected={employment.is_current}
            onChange={(selected) => {
              handleEmploymentItemChange({
                ...employment,
                is_current: selected,
                // Restore the original values
                ...(!selected &&
                  originalEmployment && {
                    end_date: originalEmployment.end_date,
                    end_date_precision: originalEmployment.end_date_precision,
                  }),
              });
            }}
            testID="employment-item-current"
          >
            {t("Currently working at this company")}
          </Toggle>
        </Stack>
      )}
    </Stack>
  );
};
